import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { Grid, TextField, Button, Select, MenuItem, InputLabel, FormControl, FormHelperText } from '@mui/material';
import FormPage from '../components/FormPage';
import { createCustomerSupportComplaint, fetchCustomers, fetchSolutions, fetchVehicles, fetchImeiAttach } from '../services/ApiService';

const CATEGORIES = ["NR", "Camera", "SD Card", "GPS", "Other"];

export function AddCustomerComplaint() {
  const navigate = useNavigate();
  const [haulierOptions, setHaulierOptions] = React.useState({});
  const [devicesOptions, setDevicesOptions] = React.useState({});
  const [vehicleData, setVehicleData] = React.useState([]);
  const [imeiData, setImeiData] = React.useState([]);
  const [trucks, setTrucks] = React.useState({});
  const [formData, setFormData] = React.useState({
    customer: "", vehicle: "", solution: "", complainant: "", category: "", details: "", status: "Pending",
  });
  const [formErrors, setFormErrors] = React.useState({});

  React.useEffect(() => {
    fetchCustomers().then((res) => {
      setHaulierOptions(res.data.reduce((acc, c) => ({ ...acc, [c.id]: c.name }), {}));
    }).catch(() => {});
    fetchSolutions().then((res) => {
      setDevicesOptions(res.data.reduce((acc, s) => ({ ...acc, [s.id]: s.name }), {}));
    }).catch(() => {});
    fetchVehicles().then((res) => setVehicleData(res.data)).catch(() => {});
    fetchImeiAttach().then((res) => setImeiData(res.data)).catch(() => {});
  }, []);

  React.useEffect(() => {
    if (formData.customer) {
      fetchVehicles(formData.customer).then((res) => {
        setTrucks(res.data.reduce((acc, v) => ({ ...acc, [v.id]: v.registration_number }), {}));
      }).catch(() => {});
    }
  }, [formData.customer]);

  React.useEffect(() => {
    if (formData.vehicle && vehicleData.length > 0 && imeiData.length > 0) {
      const matchedVehicle = vehicleData.find((v) => v.id == formData.vehicle);
      if (matchedVehicle) {
        const matchedImeiEntry = imeiData.find((i) => i.vehicle_id === matchedVehicle.id);
        if (matchedImeiEntry) {
          setFormData((prev) => ({
            ...prev,
            vehicle: matchedVehicle.id || "",
            solution: matchedImeiEntry.solution_id || "",
            imei: matchedImeiEntry.id,
          }));
        } else {
          setFormData((prev) => ({ ...prev, solution: "" }));
        }
      } else {
        setFormData((prev) => ({ ...prev, solution: "" }));
      }
    }
  }, [formData.vehicle, vehicleData, imeiData]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setFormErrors((prev) => ({ ...prev, [name]: null }));
  };

  const handleCancel = () => navigate('/customer-support', { state: { tab: 1 } });

  const handleSubmit = async (e) => {
    e.preventDefault();
    const formDataObj = new FormData();
    for (const key in formData) {
      formDataObj.append(key, formData[key]);
    }
    try {
      await createCustomerSupportComplaint(formDataObj);
      navigate('/customer-support', { state: { tab: 1, successMessage: 'Customer Support Complain added successfully' } });
    } catch (error) {
      const errData = error.response?.data;
      if (errData) {
        const newErrors = {};
        for (const field in errData) {
          newErrors[field] = Array.isArray(errData[field]) ? errData[field].join(", ") : errData[field];
        }
        setFormErrors(newErrors);
      } else {
        setFormErrors({ general: "An unexpected error occurred." });
      }
    }
  };

  return (
    <FormPage
      title="Add Customer Support Complain"
      backTo="/customer-support"
      onSubmit={handleSubmit}
      actions={<>
        <Button variant="outlined" onClick={handleCancel}>Cancel</Button>
        <Button variant="contained" type="submit">Add Complain</Button>
      </>}
    >
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth required error={!!formErrors.customer}>
            <InputLabel>Customer</InputLabel>
            <Select name="customer" value={formData.customer} onChange={handleChange} label="Customer" displayEmpty>
              <MenuItem value="" disabled>Select a customer</MenuItem>
              {Object.entries(haulierOptions).map(([key, value]) => (
                <MenuItem key={key} value={key}>{value}</MenuItem>
              ))}
            </Select>
            {formErrors.customer && <FormHelperText>{formErrors.customer}</FormHelperText>}
          </FormControl>
        </Grid>

        <Grid item xs={12} sm={6}>
          <FormControl fullWidth required error={!!formErrors.vehicle}>
            <InputLabel>Vehicle</InputLabel>
            <Select name="vehicle" value={formData.vehicle} onChange={handleChange} label="Vehicle" displayEmpty>
              <MenuItem value="" disabled>Select a vehicle</MenuItem>
              {Object.entries(trucks).map(([key, value]) => (
                <MenuItem key={key} value={key}>{value}</MenuItem>
              ))}
            </Select>
            {formErrors.vehicle && <FormHelperText>{formErrors.vehicle}</FormHelperText>}
          </FormControl>
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth label="Solution" name="solution" placeholder="Auto-filled from selected vehicle"
            value={devicesOptions[formData.solution] || ""} variant="outlined" InputProps={{ readOnly: true }}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth label="Features" name="features" placeholder="Relevant device features"
            value={formData.features || ""} onChange={handleChange}
            error={Boolean(formErrors.features)} helperText={formErrors.features}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth required label="Complainant" name="complainant" placeholder="Name of person who complained"
            value={formData.complainant} onChange={handleChange}
            error={Boolean(formErrors.complainant)} helperText={formErrors.complainant}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <FormControl fullWidth required error={Boolean(formErrors.category)}>
            <InputLabel>Category</InputLabel>
            <Select name="category" value={formData.category} onChange={handleChange} label="Category" displayEmpty>
              <MenuItem value="" disabled>Select a category</MenuItem>
              {CATEGORIES.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
            </Select>
            {formErrors.category && <FormHelperText>{formErrors.category}</FormHelperText>}
          </FormControl>
        </Grid>

        <Grid item xs={12}>
          <TextField
            fullWidth label="Details" name="details" placeholder="Describe the complaint in detail"
            value={formData.details} onChange={handleChange} multiline rows={3}
            error={Boolean(formErrors.details)} helperText={formErrors.details}
          />
        </Grid>
      </Grid>
    </FormPage>
  );
}
